import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Clock, MapPin, ArrowRight, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RelatedArticles({ category = null, title = "À lire aussi", subtitle = null, limit = 3 }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Try filtered by category first
        let picked = [];
        if (category) {
          const res = await axios.get(`${API}/blog/articles`, { params: { category } });
          picked = res.data.articles || [];
        }
        // Fallback / complete with latest articles
        if (picked.length < limit) {
          const all = await axios.get(`${API}/blog/articles`);
          const existingSlugs = new Set(picked.map((a) => a.slug));
          for (const a of all.data.articles || []) {
            if (picked.length >= limit) break;
            if (!existingSlugs.has(a.slug)) picked.push(a);
          }
        }
        setArticles(picked.slice(0, limit));
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.error(e);
      } finally {
        setLoading(false);
      }
    })();
     
  }, [category, limit]);

  if (loading || articles.length === 0) return null;

  return (
    <section className="container-tight py-20">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div className="max-w-2xl">
          <span className="chip mb-4"><BookOpen size={12} /> Journal bien-être</span>
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2b2620] tracking-tight leading-[1.1]">
            {title}
          </h2>
          {subtitle && <p className="mt-3 text-[#5c554c] leading-relaxed">{subtitle}</p>}
        </div>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#c76d4b] hover:gap-3 transition-all"
        >
          Voir tous les articles <ArrowRight size={16} />
        </Link>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {articles.map((a) => (
          <Link
            key={a.slug}
            to={`/blog/${a.slug}`}
            className="group bg-white rounded-3xl border border-[#eee5d6] overflow-hidden hover:border-[#c76d4b]/40 hover:shadow-lg transition-all"
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img
                src={a.cover}
                alt={a.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs px-2.5 py-1 rounded-full bg-[#f5ead9] text-[#5c554c] font-medium">
                  {a.category}
                </span>
                {a.location && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[#fce8d5] text-[#c76d4b] font-medium flex items-center gap-1">
                    <MapPin size={10} /> {a.location}
                  </span>
                )}
                <span className="text-xs text-[#8a7d6a] flex items-center gap-1">
                  <Clock size={11} /> {a.read_time}
                </span>
              </div>
              <h3 className="font-serif text-lg font-medium text-[#2b2620] leading-snug mb-2 group-hover:text-[#c76d4b] transition-colors line-clamp-2">
                {a.title}
              </h3>
              <p className="text-sm text-[#5c554c] leading-relaxed line-clamp-3">{a.excerpt}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#c76d4b]">
                Lire l'article <ArrowUpRight size={14} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
